<template>
  <a class="collection-skip" href="#main-content">Skip to content</a>

  <aside class="collection-rail" aria-label="App navigation">
    <RouterLink :to="landing" class="collection-mark" aria-label="Put.io home"><AppIcon name="brand" /></RouterLink>
    <nav class="collection-rail__links" aria-label="Main navigation">
      <RouterLink v-for="item in primaryLinks" :key="item.name" :to="item.to" :aria-current="isActive(item) ? 'page' : null" :title="item.label">
        <AppIcon :name="item.icon" /><span>{{ item.label }}</span>
      </RouterLink>
    </nav>
    <RouterLink to="/settings" class="collection-rail__settings" :aria-current="route.path.startsWith('/settings') ? 'page' : null" title="Settings"><AppIcon name="settings" /><span>Settings</span></RouterLink>
  </aside>

  <aside class="collection-sidebar" aria-label="Your collection">
    <RouterLink :to="landing" class="collection-wordmark">put<span>.</span>io</RouterLink>
    <template v-if="auth.canAccessLibrary">
      <h2>Your collection</h2>
      <RouterLink :to="{ name: 'library-all' }" class="collection-source" :aria-current="route.name === 'library-all' ? 'page' : null"><AppIcon name="grid" /><span>All libraries</span></RouterLink>
      <h3>Connected libraries</h3>
      <RouterLink v-for="source in sourceLinks" :key="source.key" :to="source.to" class="collection-source" :aria-current="source.active && sourceRoute ? 'page' : null">
        <span class="collection-source__dot" aria-hidden="true"></span><span>{{ source.label }}</span>
      </RouterLink>
      <RouterLink v-if="sources.jellyfinServers.length" to="/settings#sources" class="collection-source"><span class="collection-source__dot" aria-hidden="true"></span><span>Jellyfin settings</span></RouterLink>
    </template>
    <template v-if="auth.canBrowseTorrents">
      <h3>Your activity</h3>
      <RouterLink to="/discover/my-list" class="collection-source" :aria-current="route.name === 'my-list' ? 'page' : null"><AppIcon name="heart" /><span>My list</span></RouterLink>
      <RouterLink to="/discover/new-episodes" class="collection-source" :aria-current="route.name === 'new-episodes' ? 'page' : null"><AppIcon name="clock" /><span>New episodes</span></RouterLink>
      <RouterLink to="/discover/monitoring" class="collection-source" :aria-current="route.name === 'watchlist' ? 'page' : null"><AppIcon name="plus" /><span>Monitoring</span></RouterLink>
    </template>
    <RouterLink v-if="auth.canAccessDownloads" :to="{ name: 'downloads' }" class="collection-source"><AppIcon name="downloads" /><span>Downloads</span></RouterLink>
    <div class="collection-sidebar__footer"><span>One place for your next watch.</span><RouterLink to="/settings">Preferences <AppIcon name="arrow" /></RouterLink></div>
  </aside>

  <header class="collection-command" data-tv-row="header-nav">
    <RouterLink :to="landing" class="collection-mobile-brand" aria-label="Put.io home"><AppIcon name="brand" /><span>put.io</span></RouterLink>
    <button v-if="canSearch" type="button" class="collection-command__search" :aria-label="libraryRoute ? 'Search your library' : 'Search movies and shows'" @click="$emit('search')"><AppIcon name="search" /><span>{{ libraryRoute ? 'Search your library' : 'Search movies and shows' }}</span><kbd>/</kbd></button>
    <span v-else class="collection-command__location">Your collection</span>
    <div class="collection-command__actions">
      <RouterLink v-if="remote.hasActiveSession.value" :to="{ name: 'remote-home' }" class="collection-icon-button" aria-label="Open phone remote" title="Open phone remote"><AppIcon name="remote" /></RouterLink>
      <ActivityCenter />
      <HandoffIndicator />
      <AccountMenu />
    </div>
  </header>

  <nav class="collection-bottom-nav" aria-label="Mobile navigation">
    <RouterLink v-for="item in mobileLinks" :key="item.name" :to="item.to" :aria-current="isActive(item) ? 'page' : null"><AppIcon :name="item.icon" /><span>{{ item.label }}</span></RouterLink>
  </nav>
</template>

<script setup>
import { computed } from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useSourcesStore } from '@/stores/sources'
import { useRemoteShortcut } from '@/composables/useRemoteShortcut'
import { useLibrarySourceLinks } from '@/composables/useLibrarySourceLinks'
import AccountMenu from '@/components/AccountMenu.vue'
import ActivityCenter from '@/components/ActivityCenter.vue'
import HandoffIndicator from '@/components/HandoffIndicator.vue'
import AppIcon from '@/components/AppIcon.vue'

defineEmits(['search'])
const auth = useAuthStore()
const sources = useSourcesStore()
const route = useRoute()
const remote = useRemoteShortcut()
const { tabs } = useLibrarySourceLinks()
const sourceLinks = computed(() => tabs.value.filter(t => t.key !== 'all'))
const landing = computed(() => auth.canAccessLibrary ? { name: 'library-all' } : '/')
const libraryRoute = computed(() => ['library-all', 'library', 'sources-plex-browse'].includes(route.name))
const sourceRoute = computed(() => libraryRoute.value || route.name === 'youtube-home')
const canSearch = computed(() => auth.canBrowseTorrents || libraryRoute.value)
const primaryLinks = computed(() => [
  ...(auth.canBrowseTorrents ? [{ name: 'discover', label: 'Discover', icon: 'home', to: '/discover' }] : []),
  ...(auth.canAccessLibrary ? [{ name: 'library', label: 'Library', icon: 'library', to: { name: 'library-all' } }] : []),
  ...(auth.canBrowseLiveTv ? [{ name: 'live', label: 'Live TV', icon: 'live', to: '/live-tv' }] : []),
  ...(auth.canAccessDownloads ? [{ name: 'downloads', label: 'Downloads', icon: 'downloads', to: { name: 'downloads' } }] : []),
])
const mobileLinks = computed(() => [...primaryLinks.value, { name: 'settings', label: 'Settings', icon: 'settings', to: '/settings' }])
function isActive(item) {
  if (item.name === 'settings') return route.path.startsWith('/settings')
  if (item.name === 'downloads') return route.name === 'downloads'
  if (item.name === 'library') return (libraryRoute.value || route.name === 'media' || route.path.startsWith('/youtube')) && route.name !== 'downloads'
  if (item.name === 'live') return route.path.startsWith('/live-tv')
  return route.path === '/' || route.path.startsWith('/discover') || route.path.startsWith('/browse') || route.path.startsWith('/person') || route.path.startsWith('/watchlist')
}
</script>
